/* ==========================================================================
   Pabilla motion engine
   - Lenis momentum scroll (desktop only)
   - scroll reveals: [data-reveal], [data-reveal-group], [data-split]
   - scroll-linked parallax: [data-parallax-wrap] > img
   - drag-to-scroll tracks with a cursor pill: [data-drag-track]

   The page-transition veil was removed for speed: it covered every page on
   arrival and delayed every internal link by 380ms.

   Degrades to a static, fully visible page. `motion-on` is only added when
   motion is allowed, and a timed safety net releases anything the observer
   misses — setTimeout still runs in throttled or background tabs, where
   requestAnimationFrame and IntersectionObserver may not.
   ========================================================================== */
(function () {
  "use strict";

  var root = document.documentElement;
  var reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var coarse = window.matchMedia("(pointer: coarse)").matches;
  var lenis = null;

  /* ------------------------------------------------------------ scroll */
  function startScroll() {
    if (reduced || coarse || typeof window.Lenis !== "function") return;
    lenis = new window.Lenis({
      duration: 1.1,
      easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
      smoothWheel: true,
      wheelMultiplier: 0.9,
    });
    window.pabillaLenis = lenis;
    (function raf(time) { lenis.raf(time); requestAnimationFrame(raf); })(0);
  }

  /* ------------------------------------------------------------ split */
  // Wrap each word in a mask so headings rise in word by word. Recurses into
  // inline markup (<em>, <strong>) so styling inside a heading survives.
  function split(el) {
    if (el.__split) return;
    el.__split = true;
    var n = 0;

    (function walk(node) {
      [].slice.call(node.childNodes).forEach(function (child) {
        if (child.nodeType === 3) {
          var parts = child.textContent.split(/(\s+)/);
          // punctuation straight after inline markup ("<em>baked</em>,") joins the
          // previous word so it can't wrap onto a line of its own
          var prev = child.previousSibling;
          var lead = parts[0] && parts[0].match(/^[,.;:!?)’”]+/);
          if (lead && prev && prev.nodeType === 1) {
            var inners = prev.querySelectorAll(".w > span");
            var last = inners[inners.length - 1];
            if (last) {
              var p = document.createElement("span");
              p.textContent = lead[0];
              p.style.cssText = "font-style:normal;color:var(--ink,inherit)";
              last.appendChild(p);
              parts[0] = parts[0].slice(lead[0].length);
            }
          }
          var frag = document.createDocumentFragment();
          parts.forEach(function (part) {
            if (!part) return;
            if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
            var w = document.createElement("span");
            w.className = "w";
            var inner = document.createElement("span");
            inner.textContent = part;
            inner.style.setProperty("--wi", String(n++));
            w.appendChild(inner);
            frag.appendChild(w);
          });
          child.parentNode.replaceChild(frag, child);
        } else if (child.nodeType === 1 && child.tagName !== "BR") {
          walk(child);
        }
      });
    })(el);

    el.setAttribute("aria-label", el.textContent.replace(/\s+/g, " ").trim());
  }

  /* ------------------------------------------------------------ reveals */
  function startReveals() {
    var targets = [].slice.call(
      document.querySelectorAll("[data-reveal], [data-reveal-group], [data-split]")
    );
    if (!targets.length) return;

    targets.forEach(function (el) {
      if (el.hasAttribute("data-split")) split(el);
      if (el.hasAttribute("data-reveal-group")) {
        [].forEach.call(el.children, function (c, i) { c.style.setProperty("--i", String(i)); });
      }
    });

    function reveal(el) { el.classList.add("is-in"); }

    if ("IntersectionObserver" in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { reveal(e.target); io.unobserve(e.target); }
        });
      }, { rootMargin: "0px 0px -7% 0px", threshold: 0.05 });
      targets.forEach(function (t) { io.observe(t); });
    }

    // Safety net: anything above the fold, or anything the observer never
    // reached, is released on a timer rather than left hidden.
    function sweep(all) {
      var vh = window.innerHeight || 800;
      targets.forEach(function (t) {
        if (t.classList.contains("is-in")) return;
        var r = t.getBoundingClientRect();
        if (all || r.top < vh * 1.05) reveal(t);
      });
    }
    setTimeout(function () { sweep(false); }, 900);
    setTimeout(function () { sweep(true); }, 6000);
    window.addEventListener("scroll", function () { sweep(false); }, { passive: true });
  }

  /* ------------------------------------------------------------ parallax */
  function startParallax() {
    if (reduced) return;
    var items = [].slice.call(document.querySelectorAll("[data-parallax-wrap] > img"));
    if (!items.length) return;
    var ticking = false;

    function frame() {
      ticking = false;
      var vh = window.innerHeight;
      items.forEach(function (img) {
        var box = img.parentElement.getBoundingClientRect();
        if (box.bottom < -200 || box.top > vh + 200) return;
        var p = (box.top + box.height / 2 - vh / 2) / (vh / 2 + box.height / 2);
        p = Math.max(-1, Math.min(1, p));
        var speed = parseFloat(img.parentElement.getAttribute("data-parallax-speed")) || 6;
        // range [-speed, 0] of the image's own height: always inside the
        // --drift headroom, so no edge of the box is ever exposed
        img.style.transform = "translate3d(0," + ((p - 1) * speed / 2).toFixed(2) + "%,0)";
      });
    }
    function onScroll() { if (!ticking) { ticking = true; requestAnimationFrame(frame); } }

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    if (lenis) lenis.on("scroll", onScroll);
    frame();
  }

  /* ------------------------------------------------------------ drag tracks */
  function startDragTracks() {
    var tracks = [].slice.call(document.querySelectorAll("[data-drag-track]"));
    if (!tracks.length) return;

    var pill = null;
    if (!coarse) {
      pill = document.createElement("div");
      pill.className = "pm-cursor";
      pill.textContent = "Drag";
      document.body.appendChild(pill);
    }

    tracks.forEach(function (track) {
      var down = false, moved = false, startX = 0, startScroll = 0, lastX = 0, velocity = 0, raf = 0;

      function glide() {
        velocity *= 0.94;
        track.scrollLeft -= velocity;
        if (Math.abs(velocity) > 0.4) raf = requestAnimationFrame(glide);
        else track.classList.remove("is-dragging");
      }

      track.addEventListener("pointerdown", function (e) {
        if (e.pointerType !== "mouse") return; // touch uses native scrolling
        down = true; moved = false;
        startX = lastX = e.clientX;
        startScroll = track.scrollLeft;
        velocity = 0;
        cancelAnimationFrame(raf);
        track.classList.add("is-dragging");
        if (pill) pill.classList.add("is-down");
      });

      window.addEventListener("pointermove", function (e) {
        if (!down) return;
        var dx = e.clientX - startX;
        if (Math.abs(dx) > 4) moved = true;
        velocity = e.clientX - lastX;
        lastX = e.clientX;
        track.scrollLeft = startScroll - dx;
      });

      window.addEventListener("pointerup", function () {
        if (!down) return;
        down = false;
        if (pill) pill.classList.remove("is-down");
        raf = requestAnimationFrame(glide);
      });

      // a drag must not also count as a click on a card link
      track.addEventListener("click", function (e) {
        if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
      }, true);

      if (pill) {
        track.addEventListener("pointerenter", function () { pill.classList.add("is-on"); });
        track.addEventListener("pointerleave", function () { pill.classList.remove("is-on", "is-down"); });
        track.addEventListener("pointermove", function (e) {
          pill.style.left = e.clientX + "px";
          pill.style.top = e.clientY + "px";
        });
      }

      // keep Lenis from hijacking horizontal wheel/trackpad gestures here
      track.setAttribute("data-lenis-prevent-wheel", "");
    });
  }

  /* ------------------------------------------------------------ boot */
  function init() {
    if (!reduced) root.classList.add("motion-on");
    startScroll();
    startReveals();
    startParallax();
    startDragTracks();
    document.dispatchEvent(new CustomEvent("pabilla:motion-ready"));
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
